import { createMemo, type Accessor, type Component } from 'solid-js';
import { selectedStartableAudioInput, type AudioInputDescriptor } from '../lib/audioInputs';
import { audioReactiveStartReadiness, type AudioReactiveStartReadiness } from '../lib/audioReactiveStart';
import type { EffectLayout } from '../lib/effectLayout';
import { AUDIO_REACTIVE_PACKAGE_ID, AUTO_VJ_PACKAGE_ID, type EffectPackageItem } from '../lib/effectPackages';
import { createAudioReactiveHold } from '../state/effectSettings';
import type { VenueContextValue } from '../state/VenueProvider';
import { AudioReactivePanel } from './AudioReactivePanel';
import { AutoVJEffectPanel } from './AutoVJEffectPanel';

/** What the Effects page hands every tree-shaped card's panel, whichever card it is. */
export interface TreeCardProps {
  item: EffectPackageItem;
  /** The room's whole effect library, as the Effects page reads it. */
  catalog: Accessor<EffectPackageItem[]>;
  layout?: { layout: EffectLayout; elementCount?: number };
  loading: boolean;
  unavailableDetail: string;
  /** A double-click on this card asked for its start, and nobody has taken it up yet. */
  startRequested: Accessor<boolean>;
  onStartHandled: () => void;
  /** Only the Audio reactive adapter consumes this state; its card and panel share the same held stack. */
  startState?: TreeCardStartState;
}

export interface TreeCardStartState {
  settings: ReturnType<typeof createAudioReactiveHold>;
  input: Accessor<AudioInputDescriptor | undefined>;
  readiness: Accessor<AudioReactiveStartReadiness>;
}

/** The registry owns each tree card's start state, so the Effects page names no built-in card. */
export function createTreeCardStartStates(
  venue: VenueContextValue,
  catalog: Accessor<readonly EffectPackageItem[]>,
  layout: Accessor<{ layout: EffectLayout; elementCount?: number } | undefined>,
): ReadonlyMap<string, TreeCardStartState> {
  const settings = createAudioReactiveHold(venue, catalog);
  const input = createMemo(() => selectedStartableAudioInput(
    venue.audioInputs(), venue.selectedAudioInputIdentity(),
  ));
  const readiness = createMemo(() => audioReactiveStartReadiness(
    settings.hold.shown().layers ?? [], input(), Boolean(layout()), venue.controlsEnabled(),
  ));
  return new Map([[AUDIO_REACTIVE_PACKAGE_ID, { settings, input, readiness }]]);
}

/**
 * THE ADAPTER REGISTRY, and the only place in the console that knows which cards are tree-shaped (S34).
 *
 * A card's settings are either flat — a list of parameters, drawn by the one generic package panel —
 * or a tree, drawn by the one tree sheet through an adapter for that card's data model. Each entry here
 * is such a card's panel: it owns the card's Start / Stop and card-level parameters and hands its tree to
 * `SettingsTreeSheet` through its adapter (Auto VJ: sets › units › steps › layers, from the room's effect
 * documents; Audio reactive: baseline + reactive layers, from the live settings feed). The Effects page
 * asks `treeCardPanel` once per card: a panel means a tree, none means the flat package panel.
 *
 * The seam: manifests carry no field saying a card's settings are a tree, so the two built-in tree cards
 * are named here by id (web/CONTRACT-console.md S34).
 */
const TREE_CARDS: Readonly<Record<string, { panel: Component<TreeCardProps>; startActionKey: string }>> = {
  [AUDIO_REACTIVE_PACKAGE_ID]: { panel: AudioReactivePanel, startActionKey: 'start-effect' },
  [AUTO_VJ_PACKAGE_ID]: { panel: AutoVJEffectPanel, startActionKey: 'start-auto-vj' },
};

/** The card the Effects page opens on before the room reports anything running. */
export const DEFAULT_CARD_ID = AUDIO_REACTIVE_PACKAGE_ID;

/** Start actions owned by tree-shaped cards, for the Effects page's busy indicator. */
export const TREE_CARD_START_ACTION_KEYS: readonly string[] = Object.values(TREE_CARDS).map((card) => card.startActionKey);

/** The panel that draws a tree-shaped card, or `undefined` for a flat one. */
export function treeCardPanel(item: EffectPackageItem): Component<TreeCardProps> | undefined {
  return TREE_CARDS[item.packageId]?.panel;
}
